import type { JsonApiDoc, JsonApiResource, MatcherFixture } from 'tio-data/demo/types';
import type { DemoFixtureIdentity } from 'tio-employee/demo/types';
import { demoCompanyKey } from 'tio-employee/demo/identity-fixtures';

/**
 * Fixtures for the cross-product ADMIN tables — the employee directory,
 * eligibility file job logs, TAS payments, and the saved-reports listing.
 * These surfaces exist for the admin role in EVERY demo product, so they are
 * registered by the registry itself (not a product builder), replacing the
 * empty-collection catch-alls those four query types previously fell into.
 *
 * Wire notes (things that crash if omitted):
 * - paginated tables read `result.meta.page.total` → every collection doc
 *   carries pagination meta (the handler copies doc.meta onto the array)
 * - eligibility job logs must use TERMINAL statuses — the model starts a
 *   polling reload interval for QUEUED/EXECUTING_TASKS/etc., which would
 *   fire unmatched findRecord reloads
 * - eligibility cards read company.companySetting.eligibilityDefault
 *   unguarded → the shared demo company-setting carries that block
 * - report cards render only when isPublished (metadata.scope =
 *   'partner_admin'), not archived, reportType ∈ {tas,pslf,slr,syf,core},
 *   and visible to the company — visibilityLevel 'global' passes for any
 *   company id (the demo company id is non-numeric)
 *
 * The employer name and the SESSION USER's name derive from
 * {@link DemoFixtureIdentity}, so these tables always agree with the session
 * identity; the colleague rows are fixed personas shared with the TAS
 * approval-queue fixtures (same names/payroll ids), so the two surfaces tell
 * one story.
 */

const PAGE_META = (total: number) => ({ page: { total, offset: 0, limit: 50 } });

const demoEmail = (first: string, last: string) =>
  `${first}.${last}`.toLowerCase().replace(/\s+/g, '-') + '@example.com';

const employerSlug = (employerName: string) =>
  employerName.toLowerCase().replace(/[^a-z0-9]+/g, '-');

// --- /admin/employees — the employee directory ------------------------------
// Columns: payrollId, first/last name, email, participationStatus, plan.name,
// eligibilityStartDate, createdAt, statistics.user.total_accounts/total_loans.
// The first row is the session user; the EMP-1001/1042/1088 rows match the
// TAS approval-queue applicants so the two surfaces tell one story.
interface DirectoryRow {
  id: string;
  payrollId: string;
  firstName: string;
  lastName: string;
  eligibilityStartDate: string;
  createdAt: string;
  accounts: number;
  loans: number;
}

const DIRECTORY_ROWS: Omit<DirectoryRow, 'firstName' | 'lastName'>[] = [
  {
    id: 'demo-dir-employee-1',
    payrollId: 'EMP-1000',
    eligibilityStartDate: '2022-02-01',
    createdAt: '2022-01-15T09:00:00Z',
    accounts: 1,
    loans: 2,
  },
];

const COLLEAGUE_ROWS: DirectoryRow[] = [
  {
    id: 'demo-dir-employee-2',
    payrollId: 'EMP-1001',
    firstName: 'Jordan',
    lastName: 'Lee',
    eligibilityStartDate: '2023-04-01',
    createdAt: '2023-03-12T14:30:00Z',
    accounts: 1,
    loans: 3,
  },
  {
    id: 'demo-dir-employee-3',
    payrollId: 'EMP-1042',
    firstName: 'Priya',
    lastName: 'Natarajan',
    eligibilityStartDate: '2024-01-15',
    createdAt: '2023-12-20T11:15:00Z',
    accounts: 1,
    loans: 1,
  },
  {
    id: 'demo-dir-employee-4',
    payrollId: 'EMP-1088',
    firstName: 'Marcus',
    lastName: 'Brown',
    eligibilityStartDate: '2024-09-01',
    createdAt: '2024-08-05T16:45:00Z',
    accounts: 2,
    loans: 4,
  },
  {
    id: 'demo-dir-employee-5',
    payrollId: 'EMP-1112',
    firstName: 'Sofia',
    lastName: 'Alvarez',
    eligibilityStartDate: '2025-06-01',
    createdAt: '2025-05-18T08:20:00Z',
    accounts: 1,
    loans: 2,
  },
];

function directoryEmployeeResource(row: DirectoryRow): JsonApiResource {
  return {
    type: 'employee',
    id: row.id,
    attributes: {
      payrollId: row.payrollId,
      firstName: row.firstName,
      lastName: row.lastName,
      email: demoEmail(row.firstName, row.lastName),
      participationStatus: 'participating',
      isActive: true,
      eligibilityStartDate: row.eligibilityStartDate,
      createdAt: row.createdAt,
      statistics: { user: { total_accounts: row.accounts, total_loans: row.loans } },
    },
    relationships: {
      plan: { data: { type: 'plan', id: 'demo-slr-plan-1' } },
    },
  };
}

function buildEmployeeDirectoryDoc(identity: DemoFixtureIdentity): JsonApiDoc {
  const rows: DirectoryRow[] = [
    { ...DIRECTORY_ROWS[0]!, firstName: identity.firstName, lastName: identity.lastName },
    ...COLLEAGUE_ROWS,
  ];
  return {
    data: rows.map(directoryEmployeeResource),
    included: [
      // Same id + name derivation as the identity/contributions plan fixtures,
      // so the directory's plan column matches the rest of the session.
      {
        type: 'plan',
        id: 'demo-slr-plan-1',
        attributes: {
          name: `${identity.employerName} Student Loan Repayment`,
          // The employee detail page renders `plan.companyKey - plan.name`.
          companyKey: demoCompanyKey(identity.employerName),
        },
      },
    ],
    meta: PAGE_META(rows.length),
  };
}

// --- /admin/eligibility-files — eligibility file job logs --------------------
const ELIG_COUNT_SETS = [
  { created: 14, updated: 162, unchanged: 1281, deactivated: 6, reactivated: 2 },
  { created: 9, updated: 148, unchanged: 1304, deactivated: 3, reactivated: 1 },
  { created: 21, updated: 175, unchanged: 1252, deactivated: 11, reactivated: 0 },
];

function eligibilityLogResource(
  id: string,
  fileName: string,
  status: string,
  strategy: string,
  startTime: string,
  endTime: string | null,
  counts: (typeof ELIG_COUNT_SETS)[number] | null,
  previewCounts: (typeof ELIG_COUNT_SETS)[number]
): JsonApiResource {
  return {
    type: 'eligibility-file-job-log',
    id,
    attributes: {
      fileName,
      status,
      strategy,
      startTime,
      endTime,
      createdAt: startTime,
      counts: counts
        ? { ...counts, loaded: 0, createdSentEmail: counts.created }
        : {
            created: 0,
            updated: 0,
            unchanged: 0,
            deactivated: 0,
            reactivated: 0,
            loaded: 0,
            createdSentEmail: 0,
          },
      previewCounts,
      jobErrors: [],
    },
    relationships: {
      company: { data: { type: 'company', id: 'demo-tas-company-1' } },
    },
  };
}

function buildEligibilityFilesDoc(identity: DemoFixtureIdentity): JsonApiDoc {
  const slug = employerSlug(identity.employerName);
  const logs = [
    // Newest first (the table sorts -created-at): one awaiting approval, then
    // completed monthly runs. Statuses are TERMINAL/non-polling on purpose.
    eligibilityLogResource(
      'demo-elig-log-4',
      `${slug}_eligibility_2026-06-01.csv`,
      'REQUIRES_USER_APPROVAL',
      'MANUAL',
      '2026-06-01T06:00:00Z',
      null,
      null,
      { created: 12, updated: 154, unchanged: 1296, deactivated: 4, reactivated: 1 }
    ),
    eligibilityLogResource(
      'demo-elig-log-3',
      `${slug}_eligibility_2026-05-01.csv`,
      'COMPLETED',
      'AUTOMATIC',
      '2026-05-01T06:00:00Z',
      '2026-05-01T06:11:42Z',
      ELIG_COUNT_SETS[0]!,
      ELIG_COUNT_SETS[0]!
    ),
    eligibilityLogResource(
      'demo-elig-log-2',
      `${slug}_eligibility_2026-04-01.csv`,
      'COMPLETED',
      'AUTOMATIC',
      '2026-04-01T06:00:00Z',
      '2026-04-01T06:09:17Z',
      ELIG_COUNT_SETS[1]!,
      ELIG_COUNT_SETS[1]!
    ),
    eligibilityLogResource(
      'demo-elig-log-1',
      `${slug}_eligibility_2026-03-01.csv`,
      'COMPLETED',
      'MANUAL',
      '2026-03-01T06:00:00Z',
      '2026-03-01T06:14:05Z',
      ELIG_COUNT_SETS[2]!,
      ELIG_COUNT_SETS[2]!
    ),
  ];
  return { data: logs, meta: PAGE_META(logs.length) };
}

// --- /admin/tuition-assistance/payments — TAS payments -----------------------
function buildTasPaymentsDoc(): JsonApiDoc {
  const payments = [
    { id: 'demo-tas-payment-5', paymentDate: '2026-06-05', state: 'PROCESSING' },
    { id: 'demo-tas-payment-4', paymentDate: '2026-05-28', state: 'CREATED' },
    { id: 'demo-tas-payment-3', paymentDate: '2026-05-05', state: 'PAID' },
    { id: 'demo-tas-payment-2', paymentDate: '2026-04-05', state: 'PAID' },
    { id: 'demo-tas-payment-1', paymentDate: '2026-03-05', state: 'PAID' },
  ];
  return {
    data: payments.map(
      (payment): JsonApiResource => ({
        type: 'tas-payment',
        id: payment.id,
        attributes: {
          paymentDate: payment.paymentDate,
          state: payment.state,
          paymentType: 'ACH',
          createdBy: 'Tuition.io Payments',
          description: 'Tuition assistance disbursement',
          paymentFilePaths: [],
        },
        relationships: {
          company: { data: { type: 'company', id: 'demo-tas-company-1' } },
        },
      })
    ),
    meta: PAGE_META(payments.length),
  };
}

// --- /admin/reporting — the saved-reports listing -----------------------------
function reportResource(
  id: string,
  reportType: string,
  name: string,
  description: string,
  updatedAt: string
): JsonApiResource {
  return {
    type: 'report',
    id,
    attributes: {
      name,
      description,
      reportType,
      // 'global' passes isVisibleToCompany for ANY company id; scope
      // 'partner_admin' is what isPublished checks; no archivedAt.
      visibilityLevel: 'global',
      metadata: { scope: 'partner_admin', publishedAt: '2026-05-15T12:00:00Z' },
      partnerAccessConfig: [],
      searchConfig: { viewType: 'table' },
      createdAt: '2026-02-10T12:00:00Z',
      updatedAt,
    },
  };
}

function buildReportsDoc(): JsonApiDoc {
  const reports = [
    reportResource(
      'demo-report-tas',
      'tas',
      'TAS Applications by Status',
      'All tuition assistance applications grouped by approval state, refreshed daily.',
      '2026-06-02T09:30:00Z'
    ),
    reportResource(
      'demo-report-slr',
      'slr',
      'Monthly SLR Contributions',
      'Employer student-loan contributions by month, with servicer breakdown.',
      '2026-05-28T15:10:00Z'
    ),
    reportResource(
      'demo-report-pslf',
      'pslf',
      'PSLF Form Pipeline',
      'Employment certification forms by stage: drafted, employer-signed, submitted.',
      '2026-05-20T10:05:00Z'
    ),
    reportResource(
      'demo-report-core',
      'core',
      'Workforce Education Summary',
      'Cross-benefit utilization overview for the whole employee population.',
      '2026-05-12T08:45:00Z'
    ),
  ];
  return { data: reports, meta: PAGE_META(reports.length) };
}

// Detail drill-in for any eligibility row (findRecord echoes the requested
// id). A single completed-run document — the matcher vocabulary can't pick a
// per-id doc, so the pending row's detail also reads as a completed run; a
// coherent trade-off for one demo screen. Entries are linked empty (the show
// page renders an empty record list), and the riskier rich attrs (metaDiff,
// files, statusHistory) carry inert values.
function buildEligibilityLogDetailDoc(identity: DemoFixtureIdentity): JsonApiDoc {
  const detail = eligibilityLogResource(
    'demo-elig-log-detail',
    `${employerSlug(identity.employerName)}_eligibility_2026-05-01.csv`,
    'COMPLETED',
    'AUTOMATIC',
    '2026-05-01T06:00:00Z',
    '2026-05-01T06:11:42Z',
    ELIG_COUNT_SETS[0]!,
    ELIG_COUNT_SETS[0]!
  );
  detail.attributes = {
    ...detail.attributes,
    metaDiff: null,
    files: {},
    statusHistory: [],
    plans: [],
    companyMeta: [],
  };
  detail.relationships = {
    ...detail.relationships,
    eligibilityFileJobLogEntries: { data: [] },
  };
  return { data: detail };
}

// Matches ONLY report detail URLs (/api/v2/reports/<id>), never the listing.
const matchesReportDetailUrl = (url: string) => /\/api\/v2\/reports\/[^/?]+/.test(url);

/**
 * A saved-report DETAIL document in RAW wire form (dasherized attribute keys,
 * single-resource `data` object) for report drill-ins. Served on BOTH seams a
 * detail read can take: the request-manager builder path (SkipCache GET) and
 * the raw-fetch fallback — viewer code reads `data.attributes`, so the
 * listing's `{ data: [] }` shape must never answer a detail URL.
 */
export function buildReportDetailRawDoc(identity: DemoFixtureIdentity): JsonApiDoc {
  return {
    data: {
      type: 'report',
      id: 'demo-report-tas',
      attributes: {
        name: 'TAS Applications by Status',
        description: `All ${identity.employerName} tuition assistance applications grouped by approval state.`,
        'report-type': 'tas',
        'visibility-level': 'global',
        metadata: { scope: 'partner_admin', publishedAt: '2026-05-15T12:00:00Z' },
        'partner-access-config': [],
        'search-config': { viewType: 'table' },
        'created-at': '2026-02-10T12:00:00Z',
        'updated-at': '2026-06-02T09:30:00Z',
      },
    } as unknown as JsonApiResource,
  };
}

/**
 * The authored admin-table collections (+ the eligibility & report detail
 * reads). Registered by the registry AFTER product matchers (so a
 * product-specific fixture still wins) and BEFORE the remaining
 * empty-collection catch-alls.
 */
export function buildAdminTableMatchers(identity: DemoFixtureIdentity): MatcherFixture[] {
  return [
    {
      matcher: { kind: 'builder', method: 'GET', urlMatches: matchesReportDetailUrl },
      content: buildReportDetailRawDoc(identity),
    },
    {
      matcher: { kind: 'op', op: 'query', type: 'employee' },
      docs: [buildEmployeeDirectoryDoc(identity)],
    },
    {
      matcher: { kind: 'op', op: 'query', type: 'eligibility-file-job-log' },
      docs: [buildEligibilityFilesDoc(identity)],
    },
    {
      matcher: { kind: 'op', op: 'findRecord', type: 'eligibility-file-job-log' },
      docs: [buildEligibilityLogDetailDoc(identity)],
    },
    { matcher: { kind: 'op', op: 'query', type: 'tas-payment' }, docs: [buildTasPaymentsDoc()] },
    { matcher: { kind: 'op', op: 'query', type: 'report' }, docs: [buildReportsDoc()] },
  ];
}
